/**
 * Apex contact inquiry schemas (WEB-569).
 *
 * Shared by the apex ContactForm (client), the Next.js `/api/contact` proxy,
 * and `POST /api/v1/marketing/contact` on backend-api.
 */
import { z } from 'zod';

/**
 * Visible form fields — name, email, message required; subject optional.
 * Honeypot (`website`) is omitted here; the API body schema includes it.
 */
export const contactInquiryFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120, 'Name is too long'),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .email('Enter a valid email')
      .max(320, 'Email is too long'),
    subject: z
      .union([
        z.string().trim().min(1).max(200, 'Subject is too long'),
        z.literal('').transform(() => undefined),
      ])
      .optional(),
    message: z
      .string()
      .trim()
      .min(10, 'Message must be at least 10 characters')
      .max(5000, 'Message is too long'),
  })
  .strict();

export type ContactInquiryFormValues = z.infer<typeof contactInquiryFormSchema>;

/**
 * Wire body for `POST /api/v1/marketing/contact` (and the Next proxy).
 *
 * `website` is a honeypot — humans leave it empty. Non-empty values are
 * accepted by the schema so bots get a silent success without tipping them
 * off via a validation error.
 */
export const contactInquiryBodySchema = contactInquiryFormSchema
  .extend({
    website: z.string().max(500).optional().default(''),
  })
  .strict();

export type ContactInquiryBody = z.infer<typeof contactInquiryBodySchema>;
